import { useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Toast POS connection — alternative to Square for merchants on Toast.
//
// Toast's auth model differs from Square: there's no per-merchant
// consent screen. We authenticate as a partner (one set of credentials,
// approved by Toast's Partner Program), and the merchant identifies
// themselves by giving us their restaurantGuid (a UUID Toast issues
// to every account).
//
// Flow:
//   1. Customer finds their restaurantGuid on Toast dashboard:
//      Settings → Integrations → API Access → Restaurant External ID.
//   2. Pastes it into our Settings.
//   3. We validate by hitting Toast's /restaurants/v1/restaurants/{guid}
//      with our partner credentials + the guid as a header.
//   4. On success, save the guid; mark connected; future Sync now
//      hits Toast's catalog + orders endpoints.
//
// Until Toast approves your Partner Program application, the edge
// function returns 503 partner_credentials_missing — the UI surfaces
// that as a friendly "Toast partner approval pending" message.
export default function ToastConnectSection({ restaurant, onChanged }) {
  const restaurantId = restaurant?.id
  const isConnected  = !!restaurant?.toast_restaurant_guid

  const [guid, setGuid]       = useState('')
  const [busy, setBusy]       = useState(false)
  const [error, setError]     = useState(null)
  const [info, setInfo]       = useState(null)

  async function handleConnect() {
    if (busy) return
    if (!guid.trim()) {
      setError('Paste your Toast Restaurant GUID to connect.')
      return
    }
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      // Save the guid first so the edge function can read it.
      const { error: upErr } = await supabase
        .from('restaurants')
        .update({
          toast_restaurant_guid: guid.trim(),
          toast_sync_status: null,
        })
        .eq('id', restaurantId)
      if (upErr) throw upErr

      // Test the connection.
      const { data, error: fnErr } = await supabase.functions.invoke('toast-sync', {
        body: { restaurant_id: restaurantId, kind: 'test' },
      })
      if (fnErr) throw fnErr
      if (!data?.ok) {
        // Roll back the guid so the UI shows disconnected if the test failed.
        await supabase
          .from('restaurants')
          .update({ toast_restaurant_guid: null, toast_sync_status: null })
          .eq('id', restaurantId)
        if (data?.error === 'partner_credentials_missing') {
          throw new Error('Toast partner approval is still pending. We can connect customers once Toast issues partner credentials.')
        }
        throw new Error(data?.message || 'Toast rejected the connection.')
      }

      setInfo(`Connected to ${data.restaurant_name || 'your Toast restaurant'} (${data.location_count || 0} location${data.location_count === 1 ? '' : 's'}).`)
      setGuid('')
      onChanged?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ToastConnectSection] connect failed', e)
      setError(`Could not connect: ${e?.message || 'try again.'}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleSync() {
    if (busy) return
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('toast-sync', {
        body: { restaurant_id: restaurantId, kind: 'all', days: 30 },
      })
      if (fnErr) throw fnErr
      if (!data?.ok) throw new Error(data?.message || 'Sync failed.')

      const parts = []
      if (typeof data.items_synced === 'number')         parts.push(`${data.items_synced} item${data.items_synced === 1 ? '' : 's'}`)
      if (typeof data.line_items_inserted === 'number')  parts.push(`${data.line_items_inserted} sale day${data.line_items_inserted === 1 ? '' : 's'}`)
      if (typeof data.orders_synced === 'number')        parts.push(`${data.orders_synced} order${data.orders_synced === 1 ? '' : 's'}`)
      setInfo(parts.length ? `Synced ${parts.join(', ')} from Toast.` : 'Sync complete.')
      onChanged?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ToastConnectSection] sync failed', e)
      setError(`Sync failed: ${e?.message || 'try again.'}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleDisconnect() {
    if (busy) return
    if (!window.confirm('Disconnect Toast? Your synced items and sales stay; we just stop pulling new data until you reconnect.')) return
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      const { error: upErr } = await supabase
        .from('restaurants')
        .update({
          toast_restaurant_guid: null,
          toast_sync_status: null,
        })
        .eq('id', restaurantId)
      if (upErr) throw upErr
      setInfo('Disconnected. Reconnect any time by pasting a new Restaurant GUID.')
      setGuid('')
      onChanged?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ToastConnectSection] disconnect failed', e)
      setError(`Disconnect failed: ${e?.message || 'try again.'}`)
    } finally {
      setBusy(false)
    }
  }

  const lastSync = restaurant?.toast_last_synced_at
    ? new Date(restaurant.toast_last_synced_at).toLocaleString()
    : null

  return (
    <section className="mt-6 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-xs font-medium text-black/55">
            Toast POS
          </div>
          <p className="mt-1 text-xs text-black/60 leading-snug">
            Auto-sync menu and sales from Toast. Same as Square but for restaurants
            running Toast as their POS.
          </p>
        </div>
        {isConnected && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-900">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Connected
          </span>
        )}
      </div>

      {error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}
      {info && (
        <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {info}
        </div>
      )}

      {!isConnected ? (
        <div className="mt-3 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-black/70" htmlFor="toast-guid">
              Toast Restaurant GUID <span className="text-red-600">*</span>
            </label>
            <input
              id="toast-guid"
              type="text"
              value={guid}
              onChange={(e) => setGuid(e.target.value)}
              placeholder="e.g. 12345678-90ab-cdef-1234-567890abcdef"
              className="w-full rounded-lg border border-black/15 px-3 py-2 font-mono text-xs outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
            <div className="mt-1 text-[11px] text-black/55 leading-snug">
              Find this on your Toast Dashboard →{' '}
              <span className="font-medium">Settings → Integrations → API Access → Restaurant External ID</span>.
              Toast support also has it on file if you can't find it.
            </div>
          </div>

          <button
            type="button"
            onClick={handleConnect}
            disabled={busy || !guid.trim()}
            className="w-full rounded-lg bg-[#FF4F00] px-4 py-3 text-sm font-medium text-white shadow-sm hover:bg-[#E64600] active:bg-[#CC3F00] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Connecting…' : 'Connect Toast'}
          </button>
          <p className="text-[11px] text-black/55 leading-snug">
            Read-only access to your menu and sales. We never write back to Toast,
            never charge through your account, and never modify your menu.
          </p>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="rounded-lg border border-black/10 bg-black/[0.02] p-3 text-xs text-black/70 space-y-1">
            <div>
              <span className="font-medium text-black/55">Restaurant GUID:</span>{' '}
              <span className="font-mono">{restaurant.toast_restaurant_guid}</span>
            </div>
            {lastSync && (
              <div>
                <span className="font-medium text-black/55">Last sync:</span>{' '}
                {lastSync}
                {restaurant.toast_sync_status && (
                  <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                    restaurant.toast_sync_status === 'ok'      ? 'bg-emerald-100 text-emerald-900' :
                    restaurant.toast_sync_status === 'syncing' ? 'bg-blue-100 text-blue-900' :
                    'bg-red-100 text-red-900'
                  }`}>
                    {restaurant.toast_sync_status}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSync}
              disabled={busy}
              className="flex-1 rounded-lg bg-[#FF4F00] px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#E64600] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Syncing…' : 'Sync now'}
            </button>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={busy}
              className="rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Disconnect
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
